// year-round-retention.js - Tier 1 Educational
export const content = {
  heroStyle: 'default',
  tag: 'Retention Strategy',

  html: `
<p>Too many agents think of retention as something that happens during AEP. They scramble in October, then go quiet until the next October. That's not retention—that's damage control.</p>

<p>Real retention is year-round. It's the consistent presence, the regular touchpoints, the ongoing relationship that makes a client feel served all 365 days. Not just the 54 days when everyone else is calling too.</p>

<h2>Why Year-Round Matters</h2>

<p>Think about it from your client's perspective. They only hear from you when it's enrollment season—the same time every other agent, carrier, and mailer is competing for their attention. You're just more noise.</p>

<p>But an agent who calls in April? June? Just to check in? That's someone who actually cares. That's a relationship, not a transaction.</p>

<p>The data backs this up. Clients who have regular contact with their agent throughout the year are dramatically less likely to switch during AEP. The relationship you build in the "off-season" is what protects you during the on-season.</p>

<h2>The Year-Round Retention Calendar</h2>

<h3>Q1: January-March (Post-AEP/OEP)</h3>

<p><strong>Focus:</strong> New enrollee onboarding, OEP defense</p>

<p>January is when your AEP enrollees start using their coverage. It's also OEP—your first retention test.</p>

<p>Key activities:</p>
<ul>
<li>New enrollee welcome calls (first two weeks of January)</li>
<li>Check for early problems: network issues, pharmacy confusion, claims questions</li>
<li>Established client touchpoint for plan year changes</li>
<li>Solve problems before they become reasons to switch</li>
</ul>

<p>This is your foundation-setting quarter. Get it right and you've built loyalty that lasts.</p>

<h3>Q2: April-June (Mid-Year)</h3>

<p><strong>Focus:</strong> Relationship maintenance, life change catch-up</p>

<p>This is when most agents disappear. Take advantage of that:</p>

<ul>
<li>Mid-year check-in calls: "Just checking in—how's everything going?"</li>
<li>Ask about life changes: new doctors, new medications, moves, health changes</li>
<li>Update your records with anything that's changed</li>
<li>Birthday acknowledgments (always)</li>
<li>Cross-sell conversations if appropriate (dental, vision, hospital indemnity)</li>
</ul>

<p>A Q2 call often catches things clients forgot to tell you about. Maybe they switched doctors in March. Maybe they're taking a new medication. This information matters for AEP—but you won't have it if you don't ask now.</p>

<h3>Q3: July-September (Pre-AEP)</h3>

<p><strong>Focus:</strong> AEP preparation, risk identification</p>

<p>This is your retention runway. Use it:</p>

<ul>
<li>July-August: Identify high-risk clients (plan changes, low contact, past issues)</li>
<li>Review plan changes coming for next year (ANOC letters hit in September)</li>
<li>Proactive outreach to vulnerable clients: "Your plan has some changes coming—let's talk"</li>
<li>September: Pre-AEP value conversations—remind them why their current plan works</li>
<li>Schedule annual review appointments for October</li>
</ul>

<p>By the time October hits, your clients should already feel attended to. AEP isn't when you start retention—it's when you finish it.</p>

<h3>Q4: October-December (AEP)</h3>

<p><strong>Focus:</strong> Active defense + new enrollments</p>

<p>AEP is both retention and sales season:</p>

<ul>
<li>Prioritize existing client reviews before chasing new leads</li>
<li>Annual review meetings with every client possible</li>
<li>Address ANOC concerns proactively</li>
<li>Compare current plan to alternatives honestly</li>
<li>Recommend changes only when genuinely better for client</li>
<li>Document decisions and reasons for both staying and switching</li>
</ul>

<p>Yes, you'll write new business too. But your existing clients are your most valuable asset. Protect them first.</p>

<h2>Touchpoint Types</h2>

<p>Not every contact needs to be a 20-minute phone call. Mix touchpoint types:</p>

<h3>Phone Calls</h3>
<ul>
<li>Best for: Annual reviews, problem-solving, complex conversations</li>
<li>Frequency: 2-4 per year minimum</li>
<li>Duration: Can be 5 minutes or 30—depends on what's needed</li>
</ul>

<h3>Texts/Messages</h3>
<ul>
<li>Best for: Quick check-ins, reminders, birthday wishes</li>
<li>Frequency: As needed</li>
<li>Keep it personal: "Hey [name], just thinking about you—hope everything's going well!"</li>
</ul>

<h3>Emails</h3>
<ul>
<li>Best for: Information sharing, educational content, newsletters</li>
<li>Frequency: Monthly or bi-weekly</li>
<li>Don't overdo it—quality over quantity</li>
</ul>

<h3>In-Person</h3>
<ul>
<li>Best for: Complex reviews, clients who prefer face-to-face</li>
<li>Frequency: Annual minimum for key clients</li>
<li>Builds strongest relationships</li>
</ul>

<h3>Mail</h3>
<ul>
<li>Best for: Birthday cards, holiday cards, occasional check-ins</li>
<li>Frequency: 2-4 per year</li>
<li>Stands out in an increasingly digital world</li>
</ul>

<h2>The Birthday Rule</h2>

<p>This is simple but powerful: call every client on their birthday. Not text. Not email. Call.</p>

<p>Two minutes of "Happy birthday! How are you doing?" creates disproportionate loyalty. It's personal. It shows you care. And almost no other agent does it.</p>

<p>I know agents whose entire retention strategy is basically quarterly calls plus birthday calls. It works.</p>

<h2>Life Event Triggers</h2>

<p>Year-round retention means catching life changes as they happen, not once a year:</p>

<ul>
<li><strong>New diagnosis or health issue:</strong> "I'm sorry to hear that. Let's make sure your coverage is set up to support you."</li>
<li><strong>New doctor or specialist:</strong> "Let me verify they're in network and that everything is smooth."</li>
<li><strong>Move or address change:</strong> "Does your plan still work where you're living now?"</li>
<li><strong>Spouse health change:</strong> "How does this affect your situation? How can I help?"</li>
<li><strong>Financial change:</strong> "Let's make sure your coverage still fits your budget."</li>
</ul>

<p>When clients know you want to hear about changes—and that you'll help when they happen—they tell you things. Information you'd otherwise miss.</p>

<h2>Building the Habit</h2>

<p>Year-round retention only works if you actually do it. That requires systems:</p>

<ul>
<li><strong>Scheduled time:</strong> Block time weekly for retention calls, not just sales activities</li>
<li><strong>CRM reminders:</strong> Automate reminders for birthdays, anniversaries, check-in dates</li>
<li><strong>Templates:</strong> Have scripts/templates ready so calls are easy to make</li>
<li><strong>Tracking:</strong> Record every touchpoint so you know who you've contacted and when</li>
</ul>

<p>If retention isn't on your calendar, it won't happen. Make it as automatic as any other part of your business.</p>

<h2>The Compound Effect</h2>

<p>Here's what happens when you do retention year-round:</p>

<ul>
<li>Year 1: You retain more clients than usual</li>
<li>Year 2: Those clients are even more loyal, plus you retain more of year 1's new clients</li>
<li>Year 3: Compounding loyalty + less time spent replacing lost clients</li>
<li>Year 5: You have a rock-solid book that practically maintains itself</li>
</ul>

<p>The agents with the most stable, valuable books didn't get there by accident. They got there through consistent year-round attention that compounded over time.</p>
  `,

  faqs: [
    {
      question: "How often should I contact Medicare clients throughout the year?",
      answer: "At minimum, quarterly touchpoints plus birthday calls. That means 5+ contacts per year. Mix phone calls, texts, emails, and occasional mail. Q1: post-enrollment check-in. Q2: mid-year relationship call. Q3: pre-AEP preparation. Q4: annual review. More frequent is better for high-risk or high-value clients."
    },
    {
      question: "What do I talk about when calling outside of enrollment season?",
      answer: "Keep it simple: 'Just checking in—how's everything going with your coverage?' Ask about health changes, new doctors, new medications. Update your records. Birthday calls are literally just 'Happy birthday, hope you're doing well.' Clients appreciate the attention; you don't need a formal agenda."
    },
    {
      question: "How do I find time for year-round retention while also doing sales?",
      answer: "Block dedicated time weekly for retention calls. Treat it like any other important business activity—on the calendar, non-negotiable. Even 2-3 hours per week makes a significant difference. Use templates and systems to make calls efficient."
    },
    {
      question: "What's the ROI of year-round retention vs. just focusing on AEP?",
      answer: "Clients who hear from you year-round are dramatically less likely to switch during AEP. Each client you retain is worth 3-5x more over their lifetime than a new enrollment. The time spent on retention saves you the much greater time and cost of replacing lost clients."
    },
    {
      question: "Which clients should I prioritize for year-round touchpoints?",
      answer: "All clients should get at least quarterly contact. Prioritize: new enrollees (first year is highest churn risk), high-value clients (large policies, multiple products), at-risk clients (past issues, low recent contact), and clients with complex situations (duals, SNPs, chronic conditions)."
    }
  ],

  sources: [
    {
      title: "Medicare Advantage Churn Statistics",
      url: "https://www.kff.org/medicare/issue-brief/medicare-advantage-in-2024-enrollment-update-and-key-trends/",
      publisher: "KFF",
      accessedDate: "2024-12-10"
    }
  ],

  ctaDescription: `Year-round retention requires systems, not just good intentions. Pro IRP schedules your touchpoints automatically, reminds you of birthdays and anniversaries, and tracks every interaction so no client falls through the cracks.`
};

export default content;
